import { useContext, useMemo, useState } from 'react';
import {
	Box, Heading, Text, Button, VStack, HStack, Badge, Flex,
	Table, Thead, Tbody, Tr, Th, Td, Spinner, useToast, Tag,
	Tooltip, Alert, AlertIcon, Input, InputGroup, InputLeftElement,
	Divider, Stat, StatLabel, StatNumber, SimpleGrid, Icon,
	Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
	useDisclosure, RadioGroup, Radio, Stack,
} from '@chakra-ui/react';
import { FaSearch, FaPlus, FaArrowRight, FaMapMarkerAlt, FaSync } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../constants/environment';
import SessionContext from '../contexts/SessionContext';

const iconEl = (i: IconType) => <Icon as={i as unknown as React.ElementType} />;

interface TopCandidate {
	supplierId: string;
	title: string;
	score: number;
	nameUsed: string;
}

interface ReconcileResult {
	cccSupplierId: number;
	names: string[];
	action: 'id-matched' | 'auto-merged' | 'unmapped';
	bestNameUsed?: string;
	existingSupplierId?: string;
	existingSupplierTitle?: string;
	score?: number;
	topCandidates?: TopCandidate[];
}

type FilterAction = 'all' | 'unmapped' | 'auto-merged' | 'id-matched' | 'resolved';

interface ResolvedResult extends Omit<ReconcileResult, 'action'> {
	action: 'resolved';
	resolvedAt: string;
	resolution: 'merge-to-existing' | 'create-new';
}

interface ReconcileResponse {
	ready: boolean;
	dryRun?: boolean;
	message?: string;
	totals: {
		total: number;
		idMatched: number;
		autoMerged: number;
		unmapped: number;
	};
	results?: ReconcileResult[];
}

interface GooglePlaceResult {
	formattedAddress: string;
	address?: string;
	city?: string;
	state?: string;
	zip?: string;
}

interface RescoreCandidate {
	supplierId: string;
	title: string;
	originalScore: number;
	newScore: number;
	googleLocation?: GooglePlaceResult;
}

interface SupplierSearchEntry {
	_id: string;
	title: string;
	cccId: number[];
	opsId: number[];
	partstraderId: number[];
}

const scoreBadge = (score?: number) => {
	if (score === undefined || score === null) return null;
	const pct = Math.round(score * 100);
	const color = pct >= 90 ? 'green' : pct >= 70 ? 'yellow' : 'red';
	return <Badge colorScheme={color} fontSize="xs">{pct}%</Badge>;
};

const actionBadge = (action: ReconcileResult['action'] | ResolvedResult['action']) => {
	const cfg = {
		'id-matched': { label: 'Already Mapped', color: 'gray' },
		'auto-merged': { label: 'Auto-Merged', color: 'green' },
		'unmapped': { label: 'Needs Review', color: 'orange' },
		'resolved': { label: 'Resolved', color: 'purple' },
	}[action];
	return <Badge colorScheme={cfg.color} fontSize="xs">{cfg.label}</Badge>;
};

const CccVendorReconcile = () => {
	const sessionContext = useContext(SessionContext);
	const toast = useToast();
	const queryClient = useQueryClient();
	const headers = { Authorization: sessionContext?.sessionId ?? '' };

	const [dryRun, setDryRun] = useState(true);
	const [filterAction, setFilterAction] = useState<FilterAction>('all');
	const [searchQ, setSearchQ] = useState('');
	const [selectedUnmapped, setSelectedUnmapped] = useState<ReconcileResult | null>(null);
	const [rescoreData, setRescoreData] = useState<{ subject: { title: string; googleLocation?: GooglePlaceResult }; candidates: RescoreCandidate[] } | null>(null);
	const [supplierSearch, setSupplierSearch] = useState('');
	const [selectedTargetId, setSelectedTargetId] = useState<string>('');
	const [resolvedByCccId, setResolvedByCccId] = useState<Record<number, ResolvedResult>>({});
	const confirmModal = useDisclosure();

	const reconcile = useQuery<ReconcileResponse>({
		queryKey: ['ccc-vendor-reconcile', dryRun],
		queryFn: async () => {
			const { data } = await axios.get(`${API_URL}/internal/ccc-vendor-reconcile`, {
				headers,
				params: { dryRun: dryRun.toString() },
			});
			return data;
		},
		enabled: false,
	});

	const suppliersSearch = useQuery<{ suppliers: SupplierSearchEntry[] }>({
		queryKey: ['supplier-search-modal', supplierSearch],
		queryFn: async () => {
			const { data } = await axios.get(`${API_URL}/internal/suppliers`, {
				headers,
				params: { q: supplierSearch, limit: 20 },
			});
			return data;
		},
		enabled: confirmModal.isOpen && supplierSearch.length > 0,
	});

	const rescore = useMutation({
		mutationFn: async (supplierId: string) => {
			const { data } = await axios.post(
				`${API_URL}/internal/supplier-review/rescore`,
				{ supplierId, topN: 5 },
				{ headers },
			);
			return data;
		},
		onSuccess: (data) => {
			setRescoreData(data);
			toast({ title: 'Rescore complete', status: 'success', duration: 2500 });
		},
		onError: () => toast({ title: 'Rescore failed', status: 'error', duration: 3000 }),
	});

	const confirmMatch = useMutation({
		mutationFn: async (payload: { action: 'merge-to-existing' | 'create-new'; cccSupplierId: number; cccNames: string[]; targetSupplierId?: string }) => {
			const { data } = await axios.post(`${API_URL}/internal/ccc-vendor-reconcile/confirm`, payload, { headers });
			return data;
		},
		onSuccess: (data) => {
			if (selectedUnmapped) {
				const resolvedAt = new Date().toISOString();
				setResolvedByCccId(prev => ({
					...prev,
					[selectedUnmapped.cccSupplierId]: {
						...selectedUnmapped,
						action: 'resolved',
						existingSupplierId: data.supplierId ?? selectedTargetId ?? selectedUnmapped.existingSupplierId,
						existingSupplierTitle: data.supplierTitle ?? selectedUnmapped.existingSupplierTitle,
						resolution: data.action,
						resolvedAt,
					},
				}));
				queryClient.setQueryData<ReconcileResponse>(['ccc-vendor-reconcile', dryRun], old => {
					if (!old?.results) return old;
					return {
						...old,
						results: old.results.map(r => (
							r.cccSupplierId === selectedUnmapped.cccSupplierId
								? {
									...r,
									action: 'id-matched',
									existingSupplierId: data.supplierId ?? r.existingSupplierId,
									existingSupplierTitle: data.supplierTitle ?? r.existingSupplierTitle,
									topCandidates: undefined,
								}
								: r
						)),
					};
				});
			}
			confirmModal.onClose();
			setSelectedUnmapped(null);
			setRescoreData(null);
			setSelectedTargetId('');
			setFilterAction('resolved');
			toast({
				title: data.action === 'create-new' ? 'Supplier Created' : 'Merged Successfully',
				description: data.supplierTitle,
				status: 'success',
				duration: 3000,
			});
		},
		onError: (err: any) => {
			toast({
				title: 'Action failed',
				description: err.response?.data?.error || 'Unknown error',
				status: 'error',
				duration: 3000,
			});
		},
	});

	const resolvedResults = useMemo(() => (
		Object.values(resolvedByCccId).sort((a, b) => b.resolvedAt.localeCompare(a.resolvedAt))
	), [resolvedByCccId]);

	const unresolvedRemaining = useMemo(() => {
		const unresolved = reconcile.data?.results?.filter(r => r.action === 'unmapped') ?? [];
		return unresolved.filter(r => !resolvedByCccId[r.cccSupplierId]).length;
	}, [reconcile.data?.results, resolvedByCccId]);

	const filteredResults = useMemo(() => {
		const baseResults = reconcile.data?.results ?? [];
		const resolvedIds = new Set(Object.keys(resolvedByCccId).map(id => Number(id)));
		let results: Array<ReconcileResult | ResolvedResult> = [];

		if (filterAction === 'resolved') {
			results = resolvedResults;
		} else {
			const withoutResolvedUnmapped = baseResults.filter(r => !(r.action === 'unmapped' && resolvedIds.has(r.cccSupplierId)));
			if (filterAction === 'all') {
				results = [...resolvedResults, ...withoutResolvedUnmapped];
			} else {
				results = withoutResolvedUnmapped.filter(r => r.action === filterAction);
			}
		}

		if (searchQ) {
			const q = searchQ.toLowerCase();
			results = results.filter(r =>
				r.names.some(n => n.toLowerCase().includes(q)) ||
				r.cccSupplierId.toString().includes(q) ||
				r.existingSupplierTitle?.toLowerCase().includes(q)
			);
		}

		return results;
	}, [reconcile.data?.results, filterAction, resolvedByCccId, resolvedResults, searchQ]);

	const openConfirmModal = (result: ReconcileResult) => {
		setSelectedUnmapped(result);
		setRescoreData(null);
		setSelectedTargetId(result.topCandidates?.[0]?.supplierId ?? '');
		setSupplierSearch('');
		confirmModal.onOpen();
	};

	const handleRescore = () => {
		if (!selectedUnmapped) return;
		const targetId = selectedUnmapped.topCandidates?.[0]?.supplierId;
		if (targetId) {
			rescore.mutate(targetId);
		}
	};

	return (
		<Box p={8}>
			<Heading size="lg" mb={2}>CCC Vendor Reconciliation</Heading>
			<Text color="gray.600" mb={6}>
				Reconcile vendors from the <code>cccVendors</code> collection to canonical suppliers.
				Vendors above the match threshold are auto-merged; others require manual review.
			</Text>

			<HStack mb={6} spacing={4}>
				<Button
					colorScheme="blue"
					leftIcon={iconEl(FaSync)}
					onClick={() => reconcile.refetch()}
					isLoading={reconcile.isFetching}
				>
					{dryRun ? 'Preview (Dry Run)' : 'Run Reconciliation'}
				</Button>
				<HStack>
					<Text fontSize="sm" color="gray.600">Mode:</Text>
					<Button
						size="sm"
						variant={dryRun ? 'solid' : 'outline'}
						colorScheme={dryRun ? 'yellow' : 'gray'}
						onClick={() => setDryRun(true)}
					>
						Dry Run
					</Button>
					<Button
						size="sm"
						variant={!dryRun ? 'solid' : 'outline'}
						colorScheme={!dryRun ? 'green' : 'gray'}
						onClick={() => setDryRun(false)}
					>
						Live
					</Button>
				</HStack>
			</HStack>

			{reconcile.data && (
				<>
					{reconcile.data.dryRun && (
						<Alert status="warning" mb={4}>
							<AlertIcon />
							Dry run mode — no changes were made. Switch to Live mode to apply auto-merges.
						</Alert>
					)}

					<SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} mb={6}>
						<Stat p={4} borderWidth={1} borderRadius="md">
							<StatLabel>Total CCC Vendors</StatLabel>
							<StatNumber>{reconcile.data.totals.total}</StatNumber>
						</Stat>
						<Stat p={4} borderWidth={1} borderRadius="md" bg="gray.50">
							<StatLabel>Already Mapped</StatLabel>
							<StatNumber>{reconcile.data.totals.idMatched}</StatNumber>
						</Stat>
						<Stat p={4} borderWidth={1} borderRadius="md" bg="green.50">
							<StatLabel>Auto-Merged</StatLabel>
							<StatNumber color="green.600">{reconcile.data.totals.autoMerged}</StatNumber>
						</Stat>
						<Stat p={4} borderWidth={1} borderRadius="md" bg="orange.50">
							<StatLabel>Needs Review</StatLabel>
							<StatNumber color="orange.600">{unresolvedRemaining}</StatNumber>
						</Stat>
						<Stat p={4} borderWidth={1} borderRadius="md" bg="purple.50">
							<StatLabel>Resolved</StatLabel>
							<StatNumber color="purple.600">{resolvedResults.length}</StatNumber>
						</Stat>
					</SimpleGrid>

					<HStack mb={4} spacing={4}>
						<InputGroup maxW="300px">
							<InputLeftElement pointerEvents="none">
								<Icon as={FaSearch as unknown as React.ElementType} color="gray.400" />
							</InputLeftElement>
							<Input
								placeholder="Search by name or ID"
								value={searchQ}
								onChange={e => setSearchQ(e.target.value)}
							/>
						</InputGroup>
						<HStack>
							<Text fontSize="sm" color="gray.600">Filter:</Text>
							{(['all', 'unmapped', 'resolved', 'auto-merged', 'id-matched'] as const).map(f => (
								<Button
									key={f}
									size="xs"
									variant={filterAction === f ? 'solid' : 'outline'}
									colorScheme={filterAction === f ? 'blue' : 'gray'}
									onClick={() => setFilterAction(f)}
								>
									{f === 'all'
										? 'All'
										: f === 'unmapped'
											? 'Needs Review'
											: f === 'resolved'
												? 'Resolved'
												: f === 'auto-merged'
													? 'Auto-Merged'
													: 'Already Mapped'}
								</Button>
							))}
						</HStack>
					</HStack>

					<Box overflowX="auto">
						<Table variant="simple" size="sm">
							<Thead>
								<Tr>
									<Th>CCC ID</Th>
									<Th>Names</Th>
									<Th>Status</Th>
									<Th>Match</Th>
									<Th>Score</Th>
									<Th>Top Candidates</Th>
									<Th>Action</Th>
								</Tr>
							</Thead>
							<Tbody>
								{filteredResults.map(r => (
									<Tr key={`${r.cccSupplierId}-${r.action}`} bg={r.action === 'unmapped' ? 'orange.50' : r.action === 'resolved' ? 'purple.50' : undefined}>
										<Td fontFamily="mono" fontSize="xs">{r.cccSupplierId}</Td>
										<Td>
											<VStack align="start" spacing={0}>
												{r.names.slice(0, 2).map((n, i) => (
													<Text key={i} fontSize="sm" maxW="250px" isTruncated>{n}</Text>
												))}
												{r.names.length > 2 && (
													<Tooltip label={r.names.slice(2).join(', ')}>
														<Text fontSize="xs" color="gray.500">+{r.names.length - 2} more</Text>
													</Tooltip>
												)}
											</VStack>
										</Td>
										<Td>{actionBadge(r.action)}</Td>
										<Td>
											{r.existingSupplierTitle && (
												<Tooltip label={r.existingSupplierId}>
													<Text fontSize="sm" maxW="200px" isTruncated>{r.existingSupplierTitle}</Text>
												</Tooltip>
											)}
										</Td>
										<Td>{scoreBadge(r.score)}</Td>
										<Td>
											{r.action === 'unmapped' && r.topCandidates && (
												<VStack align="start" spacing={1}>
													{r.topCandidates.slice(0, 2).map((c, i) => (
														<HStack key={i} spacing={1}>
															<Text fontSize="xs" maxW="150px" isTruncated>{c.title}</Text>
															{scoreBadge(c.score)}
														</HStack>
													))}
												</VStack>
											)}
										</Td>
										<Td>
											{r.action === 'unmapped' && (
												<Button
													size="xs"
													colorScheme="blue"
													onClick={() => openConfirmModal(r)}
												>
													Review
												</Button>
											)}
											{r.action === 'resolved' && (
												<Text fontSize="xs" color="purple.600" fontWeight="semibold">Done</Text>
											)}
										</Td>
									</Tr>
								))}
								{filteredResults.length === 0 && (
									<Tr>
										<Td colSpan={7}>
											<Text color="gray.500" textAlign="center">No results match filters.</Text>
										</Td>
									</Tr>
								)}
							</Tbody>
						</Table>
					</Box>
				</>
			)}

			{!reconcile.data && !reconcile.isFetching && (
				<Alert status="info">
					<AlertIcon />
					Click "Preview" to analyze CCC vendors and see reconciliation results.
				</Alert>
			)}

			{reconcile.isFetching && (
				<Flex justify="center" p={8}>
					<Spinner size="lg" />
				</Flex>
			)}

			<Modal isOpen={confirmModal.isOpen} onClose={confirmModal.onClose} size="xl">
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Review CCC Vendor Match</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						{selectedUnmapped && (
							<VStack align="stretch" spacing={4}>
								<Box>
									<Text fontWeight="bold" mb={1}>CCC Vendor</Text>
									<Tag colorScheme="blue" mr={2}>ID: {selectedUnmapped.cccSupplierId}</Tag>
									<VStack align="start" spacing={0} mt={2}>
										{selectedUnmapped.names.map((n, i) => (
											<Text key={i} fontSize="sm">{n}</Text>
										))}
									</VStack>
								</Box>

								<Divider />

								<Box>
									<HStack mb={2}>
										<Text fontWeight="bold">Top Candidates</Text>
										<Button
											size="xs"
											leftIcon={iconEl(FaMapMarkerAlt)}
											onClick={handleRescore}
											isLoading={rescore.isPending}
										>
											Google Rescore
										</Button>
									</HStack>

									{rescoreData ? (
										<VStack align="stretch" spacing={2}>
											<Text fontSize="xs" color="gray.600">
												Subject location: {rescoreData.subject.googleLocation?.formattedAddress ?? '(no match)'}
											</Text>
											<RadioGroup value={selectedTargetId} onChange={setSelectedTargetId}>
												<Stack>
													{rescoreData.candidates.map(c => (
														<Box key={c.supplierId} p={2} borderWidth={1} borderRadius="md">
															<Radio value={c.supplierId}>
																<HStack>
																	<Text fontSize="sm" fontWeight="medium">{c.title}</Text>
																	<Tooltip label={`Original: ${Math.round(c.originalScore * 100)}%`}>
																		{scoreBadge(c.newScore)}
																	</Tooltip>
																</HStack>
																<Text fontSize="xs" color="gray.600">
																	{c.googleLocation?.formattedAddress ?? ''}
																</Text>
															</Radio>
														</Box>
													))}
												</Stack>
											</RadioGroup>
										</VStack>
									) : (
										<RadioGroup value={selectedTargetId} onChange={setSelectedTargetId}>
											<Stack>
												{selectedUnmapped.topCandidates?.map(c => (
													<Radio key={c.supplierId} value={c.supplierId}>
														<HStack>
															<Text fontSize="sm">{c.title}</Text>
															{scoreBadge(c.score)}
														</HStack>
													</Radio>
												))}
											</Stack>
										</RadioGroup>
									)}
								</Box>

								<Divider />

								<Box>
									<Text fontWeight="bold" mb={2}>Or search for another supplier</Text>
									<InputGroup size="sm">
										<InputLeftElement pointerEvents="none">
											<Icon as={FaSearch as unknown as React.ElementType} color="gray.400" />
										</InputLeftElement>
										<Input
											placeholder="Search suppliers..."
											value={supplierSearch}
											onChange={e => setSupplierSearch(e.target.value)}
										/>
									</InputGroup>
									{suppliersSearch.data && supplierSearch && (
										<Box mt={2} maxH="150px" overflowY="auto">
											<RadioGroup value={selectedTargetId} onChange={setSelectedTargetId}>
												<Stack>
													{suppliersSearch.data.suppliers.map(s => (
														<Radio key={s._id} value={s._id}>
															<Text fontSize="sm">{s.title}</Text>
														</Radio>
													))}
												</Stack>
											</RadioGroup>
										</Box>
									)}
								</Box>
							</VStack>
						)}
					</ModalBody>
					<ModalFooter>
						<HStack spacing={3}>
							<Button size="action" variant="outlineGray" onClick={confirmModal.onClose}>Cancel</Button>
							<Button
								size="action"
								colorScheme="green"
								leftIcon={iconEl(FaPlus)}
								isLoading={confirmMatch.isPending}
								onClick={() => {
									if (!selectedUnmapped) return;
									confirmMatch.mutate({
										action: 'create-new',
										cccSupplierId: selectedUnmapped.cccSupplierId,
										cccNames: selectedUnmapped.names,
									});
								}}
							>
								Create New Supplier
							</Button>
							<Button
								size="action"
                            bg="#0071E3"
                            color="white"
                            _hover={{ bg: '#3544a0' }}
								leftIcon={iconEl(FaArrowRight)}
								isDisabled={!selectedTargetId}
								isLoading={confirmMatch.isPending}
								onClick={() => {
									if (!selectedUnmapped || !selectedTargetId) return;
									confirmMatch.mutate({
										action: 'merge-to-existing',
										cccSupplierId: selectedUnmapped.cccSupplierId,
										cccNames: selectedUnmapped.names,
										targetSupplierId: selectedTargetId,
									});
								}}
							>
								Merge to Selected
							</Button>
						</HStack>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Box>
	);
};

export default CccVendorReconcile;
